'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { NavButton } from '@/components/ui/nav-button';
import { strobeOnHover } from '@/components/ui/nav-strobe';
import { CatalogMenu } from '@/components/ui/home/catalog-menu';
import { Breadcrumb } from '@/components/ui/home/breadcrumb';
import { CartButton } from '@/components/ui/store/cart-button';
import { ROUTES } from '@/lib/constants/store';
import { useWishlist } from '@/contexts/WishlistContext';
import { BreadcrumbItem } from '@/components/ui/layout/fixed-layout';
import { features } from '@/lib/config/features';
import { useTenant } from '@/contexts/TenantContext';

interface HeaderProps {
  onCatalogMenuToggle?: () => void;
  isCatalogMenuOpen?: boolean;
  onLeftMenuToggle?: () => void;
  isLeftMenuOpen?: boolean;
  onCartToggle?: () => void;
  title?: string;
  breadcrumbs?: BreadcrumbItem[];
  pageKey?: string;
  isMobileCheck?: (isMobile: boolean) => void;
  inverted?: boolean;
}

interface CatalogMenuItemProps {
  item: { label: string; href: string };
  index: number;
  isCatalogMenuOpen: boolean;
  isLeftMenuOpen: boolean;
  isActive: boolean;
}

// One link of the expanded menu; fades in on its own delay when the menu opens
function CatalogMenuItem({ item, index, isCatalogMenuOpen, isLeftMenuOpen, isActive }: CatalogMenuItemProps) {
  return (
    <div
      className={`opacity-0 ${isCatalogMenuOpen || isLeftMenuOpen ? 'animate-fade-in' : ''} overflow-hidden`}
      style={{
        animationDelay: `${80 + index * 40}ms`,
        animationFillMode: 'forwards',
      }}
    >
      <Link
        href={item.href}
        className={`nav-button nav-strobe inline-block ${isActive ? 'nav-strobe-active' : ''}`}
        {...strobeOnHover}
      >
        {item.label}
      </Link>
    </div>
  );
}

export function Header({
  onCatalogMenuToggle,
  isCatalogMenuOpen = false,
  onLeftMenuToggle,
  isLeftMenuOpen = false,
  onCartToggle,
  breadcrumbs,
  inverted = false,
}: HeaderProps) {
  const pathname = usePathname();
  const { items: savedItems } = useWishlist();
  const { navCounts } = useTenant();

  const hasCount = (key: keyof NonNullable<typeof navCounts>) =>
    navCounts?.[key] === undefined || (navCounts[key] ?? 0) > 0;

  const catalogMainItems = [
    { label: 'ABOUT', href: '/about' },
    ...(hasCount('press') ? [{ label: 'PRESS & MEDIA', href: '/press' }] : []),
    { label: 'ANALYTICS', href: '/insights/analytics' },
    { label: 'LEADERBOARD', href: '/insights/leaderboard' },
  ];

  // Catalog menu items - æ
  const catalogMenuItems = [
    { label: 'INDEX', href: '/catalog' },
    { label: 'COLLECT', href: '/catalog/collect' },
    ...(hasCount('series') ? [{ label: 'SERIES', href: '/catalog/collections' }] : []),
    ...(hasCount('oneOfOnes') ? [{ label: 'ONE OF ONES', href: '/catalog/one-of-ones' }] : []),
    ...(hasCount('installations') ? [{ label: 'IMMERSIVE', href: '/catalog/immersive' }] : []),
    ...(hasCount('exhibitions') ? [{ label: 'EXHIBITIONS', href: '/catalog/exhibitions' }] : []),
    ...(hasCount('awards') ? [{ label: 'AWARDS', href: '/catalog/awards' }] : []),
  ];

  const checkActive = (items: { label: string; href: string }[], path: string) => {
    // pathname.startsWith('/catalog')
    // return items.some(item => item.href === path);
    return items.some(item => path === item.href || path.startsWith(`${item.href}/`));
  };

  // Function to check if a path is active
  const isActive = (path: string) => {
    // For exact matches like /about
    if (path === pathname) return true;

    // For catalog items, check if the current path starts with the item path
    // but only if the item path is not just '/catalog' (to avoid matching all catalog subpages)
    if (path !== '/catalog' && pathname.startsWith(path)) return true;

    // Special case for /catalog
    if (path === '/catalog' && pathname === '/catalog') return true;

    return false;
  };

  // hidden md:block

  return (
    <div className={`transition-all duration-300 ease-in-out ${inverted ? 'bg-white' : 'bg-black'} main-header`}>
      {/* Main Header */}

      <header
        className="px-2 pt-2 pb-[7px] hidden lg:flex justify-between 
       items-center relative border-b border-transparent transition-all
       duration-300 ease-in-out
       "
      >
        <div className="flex space-x-1">
          <CatalogMenu
            onToggle={onLeftMenuToggle}
            isOpen={isLeftMenuOpen}
            isActive={checkActive(catalogMainItems, pathname)}
            label={'INSIGHTS'}
            isLeft={true}
          />
          {/* <Link href="/press" className="inline-flex z-50 relative" prefetch={false}>
            <NavButton className="pointer-events-auto" isActive={isActive('/press')}>
              PRESS
            </NavButton>
          </Link>
          <Link href="/about" className="inline-flex z-50 relative" prefetch={false}>
            <NavButton className="pointer-events-auto" isActive={isActive('/about')}>
              ABOUT
            </NavButton>
          </Link>
          <Link href="/insights" className="inline-flex z-50 relative" prefetch={false}>
            <NavButton className="pointer-events-auto" isActive={isActive('/insights')}>
              INSIGHTS
            </NavButton>
          </Link> */}
        </div>

        <div className="absolute left-1/2 transform -translate-x-1/2">
          <Breadcrumb breadcrumbs={breadcrumbs} inverted={inverted} />
        </div>

        <div className="flex space-x-1">
          {/* todo: <NavButton>GOODS</NavButton> */}
          {/* <Link href="/writings" className="inline-flex z-50 relative" prefetch={false}>
            <NavButton className="pointer-events-auto" isActive={isActive('/writings')}>
              WRITINGS
            </NavButton>
          </Link> */}
          <CartButton onClick={onCartToggle} />
          {savedItems.length > 0 && (
            <Link href={ROUTES.WISHLIST} className="inline-flex z-50 relative" prefetch={false}>
              <NavButton className="pointer-events-auto">SAVED {savedItems.length}</NavButton>
            </Link>
          )}
          {features.store && (
            <Link href="/store" className="inline-flex z-50 relative" prefetch={false}>
              <NavButton className="pointer-events-auto" isActive={isActive('/store')}>
                STORE
              </NavButton>
            </Link>
          )}
          <CatalogMenu
            onToggle={onCatalogMenuToggle}
            isOpen={isCatalogMenuOpen}
            isActive={checkActive(catalogMenuItems, pathname)}
            label="CATALOG"
          />
        </div>
      </header>

      {/* Left Menu */}
      <div
        className={`hidden lg:flex overflow-hidden transition-all duration-300 ease-in-out
           ${isLeftMenuOpen ? 'animate-slide-down' : ''}`}
        style={{
          maxHeight: isLeftMenuOpen ? '60px' : '0',
          opacity: isLeftMenuOpen ? '1' : '0',
          // marginTop: isLeftMenuOpen ? '2px' : '0'
        }}
      >
        <div className="flex justify-start px-2 pt-0 pb-0 w-full">
          <div className="flex space-x-2 overflow-x-auto overflow-y-hidden py-0 pr-2 ">
            {catalogMainItems.map((item, index) => {
              return (
                <CatalogMenuItem
                  key={index}
                  item={item}
                  index={index}
                  isCatalogMenuOpen={isCatalogMenuOpen}
                  isLeftMenuOpen={isLeftMenuOpen}
                  isActive={isActive(item.href)}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Right Menu */}
      <div
        className={`hidden lg:flex overflow-hidden transition-all duration-300 ease-in-out
           ${isCatalogMenuOpen ? 'animate-slide-down' : ''}`}
        style={{
          maxHeight: isCatalogMenuOpen ? '60px' : '0',
          opacity: isCatalogMenuOpen ? '1' : '0',
          // marginTop: isCatalogMenuOpen ? '2px' : '0'
        }}
      >
        <div className="flex justify-end px-2 pt-0 pb-0 w-full">
          <div className="flex space-x-2 overflow-x-auto overflow-y-hidden py-0 pl-2 ">
            {catalogMenuItems.map((item, index) => {
              return (
                <CatalogMenuItem
                  key={index}
                  item={item}
                  index={index}
                  isCatalogMenuOpen={isCatalogMenuOpen}
                  isLeftMenuOpen={isLeftMenuOpen}
                  isActive={isActive(item.href)}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Mobile Header */}
      <header className="px-2 pt-2 pb-0 flex lg:hidden justify-between items-center relative border-b border-transparent transition-all duration-300 ease-in-out ">
        <div className="pl-0">
          <Breadcrumb breadcrumbs={breadcrumbs} mobile={true} inverted={inverted} />
        </div>
        <div className="flex space-x-1">
          <CartButton onClick={onCartToggle} />
          {savedItems.length > 0 && (
            <Link href={ROUTES.WISHLIST} className="inline-flex z-50 relative" prefetch={false}>
              <NavButton className="pointer-events-auto">SAVED {savedItems.length}</NavButton>
            </Link>
          )}
          <CatalogMenu
            onToggle={onCatalogMenuToggle}
            isOpen={isCatalogMenuOpen}
            isActive={pathname.startsWith('/catalog') || pathname.startsWith('/store')}
            isMobile={true}
            label="CATALOG"
          />
        </div>
      </header>

      {/* Integrated Catalog Menu - expands within navbar */}
      <div
        className={`flex lg:hidden flex-col overflow-hidden transition-all duration-300 ease-in-out ${isCatalogMenuOpen ? 'animate-slide-down' : ''}`}
        style={{
          maxHeight: isCatalogMenuOpen ? '270px' : '0',
          opacity: isCatalogMenuOpen ? '1' : '0',
          // marginTop: isCatalogMenuOpen ? '2px' : '0'
        }}
      >
        <div className="bg-[#101010] m-2 rounded-md">
          <div className="flex justify-between p-1 mt-1  flex-row w-full relative uppercase text-[11px] font-normal">
            <div className="flex flex-col items-start pl-2 flex-1 ">[ INSIGHTS ]</div>
            <div className="flex flex-col items-end pr-2 flex-1 ">[ CATALOG RASIONNE ]</div>
          </div>
          <div className="border-b border-white/20 m-1 mx-3" />
          <div className="flex justify-between p-1 pt-0 flex-row w-full">
            <div className="flex flex-col items-start pl-2 flex-1 ">
              {catalogMainItems.map((item, index) => {
                return (
                  <CatalogMenuItem
                    key={index}
                    item={item}
                    index={index}
                    isCatalogMenuOpen={isCatalogMenuOpen}
                    isLeftMenuOpen={isLeftMenuOpen}
                    isActive={isActive(item.href)}
                  />
                );
              })}
            </div>
            <div className="flex flex-col items-end pr-2 flex-1 ">
              {catalogMenuItems.map((item, index) => {
                return (
                  <CatalogMenuItem
                    key={index}
                    item={item}
                    index={index}
                    isCatalogMenuOpen={isCatalogMenuOpen}
                    isLeftMenuOpen={isLeftMenuOpen}
                    isActive={isActive(item.href)}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
